/**
 * useFormAutoSave(resourceUrl, values, options) — enregistrement automatique d'un formulaire entier.
 *
 * - Debounce 800 ms après la dernière modification, PATCH avec UNIQUEMENT les champs modifiés.
 * - File d'attente locale (localStorage, clé par resourceUrl) : survit à un rechargement.
 * - Réessais automatiques (2 s, 5 s, 10 s) puis état « offline ».
 * - Écoute online/offline ; renvoie la file au retour du réseau.
 * - beforeunload si des modifications sont en attente.
 * - 422 → erreurs par champ (retournées) sans relance ; 409 → état « conflict ».
 *
 * Retourne { status, pending, saveNow, errors }.
 * status : 'idle' | 'saving' | 'saved' | 'offline' | 'conflict'
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import client from '../services/api';

const RETRY_DELAYS = [2000, 5000, 10000];
const emit = (state) => window.dispatchEvent(new CustomEvent('vd-save', { detail: { state } }));
const qKey = (url) => `vd-autosave::${url}`;

function loadQueue(url) {
  try { return JSON.parse(localStorage.getItem(qKey(url))) || {}; } catch { return {}; }
}
function persistQueue(url, obj) {
  if (obj && Object.keys(obj).length) localStorage.setItem(qKey(url), JSON.stringify(obj));
  else localStorage.removeItem(qKey(url));
}

export function useFormAutoSave(resourceUrl, values, options = {}) {
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});
  const queue = useRef(loadQueue(resourceUrl));
  const baseline = useRef(options.initial ? { ...options.initial } : null);
  const version = useRef(null);
  const debounceT = useRef(null);
  const retryT = useRef(null);
  const retryN = useRef(0);
  const [pending, setPending] = useState(Object.keys(queue.current).length > 0);

  const syncPending = () => setPending(Object.keys(queue.current).length > 0);

  const flush = useCallback(async () => {
    if (!Object.keys(queue.current).length) { setStatus((s) => (s === 'saving' ? 'saved' : s)); return; }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { setStatus('offline'); emit('offline'); return; }
    const payload = { ...queue.current };
    if (version.current) payload._base_updated_at = version.current;
    setStatus('saving'); emit('saving');
    try {
      const updated = await client.patch(resourceUrl, payload).then((r) => r.data);
      Object.keys(payload).forEach((k) => {
        if (k !== '_base_updated_at' && JSON.stringify(queue.current[k]) === JSON.stringify(payload[k])) delete queue.current[k];
      });
      persistQueue(resourceUrl, queue.current);
      if (updated && updated.updated_at) version.current = updated.updated_at;
      if (baseline.current) Object.assign(baseline.current, payload);
      retryN.current = 0; setErrors({}); syncPending();
      if (Object.keys(queue.current).length) { flush(); } else { setStatus('saved'); emit('saved'); }
    } catch (err) {
      const code = err.code || err.response?.status;
      if (code === 422) {
        const detail = err.response?.data?.detail;
        const fieldErrors = {};
        (Array.isArray(detail) ? detail : []).forEach((d) => { const f = d.loc?.[d.loc.length - 1]; if (f) fieldErrors[f] = d.msg; });
        setErrors(Object.keys(fieldErrors).length ? fieldErrors : { _global: err.message || 'Validation refusée' });
        Object.keys(payload).forEach((k) => { if (k !== '_base_updated_at') delete queue.current[k]; });
        persistQueue(resourceUrl, queue.current); syncPending();
        setStatus('idle');
      } else if (code === 409) {
        setStatus('conflict'); emit('conflict');
      } else if (retryN.current < RETRY_DELAYS.length) {
        const delay = RETRY_DELAYS[retryN.current]; retryN.current += 1;
        setStatus('offline'); emit('offline');
        clearTimeout(retryT.current); retryT.current = setTimeout(flush, delay);
      } else {
        setStatus('offline'); emit('offline');
      }
    }
  }, [resourceUrl]);

  const saveNow = useCallback(() => { retryN.current = 0; clearTimeout(retryT.current); flush(); }, [flush]);

  useEffect(() => {
    if (!values) return undefined;
    if (!baseline.current) { baseline.current = { ...values }; return undefined; }
    const changed = {};
    Object.keys(values).forEach((k) => {
      if (JSON.stringify(values[k]) !== JSON.stringify(baseline.current[k])) changed[k] = values[k];
    });
    if (!Object.keys(changed).length) return undefined;
    queue.current = { ...queue.current, ...changed };
    persistQueue(resourceUrl, queue.current); syncPending();
    clearTimeout(debounceT.current);
    debounceT.current = setTimeout(() => { retryN.current = 0; flush(); }, 800);
    return () => clearTimeout(debounceT.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values)]);

  useEffect(() => {
    const onOnline = () => { if (Object.keys(queue.current).length) { retryN.current = 0; flush(); } };
    const onOffline = () => { if (Object.keys(queue.current).length) { setStatus('offline'); emit('offline'); } };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, [flush]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (Object.keys(queue.current).length) { e.preventDefault(); e.returnValue = ''; return ''; }
      return undefined;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => { if (Object.keys(queue.current).length) flush(); /* eslint-disable-next-line */ }, []);

  return { status, pending, saveNow, errors };
}

/** Bouton de secours : discret quand tout est enregistré, principal dès qu'il reste des modifications. */
export function AutoSaveButton({ status, pending, saveNow, className = '' }) {
  const unsaved = pending || status === 'offline' || status === 'conflict';
  if (!unsaved) {
    return (
      <button type="button" disabled data-testid="autosave-button" data-state="saved"
        className={`vd-btn h-9 opacity-60 cursor-default ${className}`}>Enregistré ✓</button>
    );
  }
  return (
    <button type="button" onClick={saveNow} data-testid="autosave-button" data-state="unsaved"
      className={`vd-btn vd-btn-primary h-9 ${className}`}>Enregistrer maintenant</button>
  );
}
